import React, { useState, useEffect, useRef } from 'react'
import { Icon } from '../Icon'

interface Option {
  iconName?: string
  value: string
  label: string
}

interface Props {
  options: Option[]
  title: string
  placeholder?: string
  error?: string | undefined
  iconName?: string
  labelClass?: string
  selectedOptionClass?: string
  value?: string[]
  onChange?: (selectedValues: string[]) => void // Updated onChange prop
  onBlur?: () => void
  disabled?: boolean
}

const UIMultiSelect: React.FC<Props> = ({
  options,
  title,
  placeholder,
  error,
  iconName,
  labelClass,
  selectedOptionClass,
  value,
  onChange, // Updated onChange prop
  onBlur,
  disabled,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([])
  const wrapperRef = useRef<HTMLDivElement>(null)

  const [isOpen, setIsOpen] = useState<boolean>(false)

  useEffect(() => {
    if (value) {
      const foundOptions = options.filter((option) =>
        value.includes(option.value)
      )
      setSelectedOptions(foundOptions)
    }
  }, [value, options])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        if (isOpen) {
          setIsOpen(false)
          if (onBlur) {
            onBlur()
          }
        }
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isOpen, onBlur])

  const toggleSelect = () => {
    setIsOpen(!isOpen)
  }

  const handleOptionClick = (option: Option) => {
    const isOptionSelected = selectedOptions.some(
      (selectedOption) => selectedOption.value === option.value
    )
    let newSelectedOptions: Option[] = []
    if (isOptionSelected) {
      newSelectedOptions = selectedOptions.filter(
        (selectedOption) => selectedOption.value !== option.value
      )
    } else {
      newSelectedOptions = [...selectedOptions, option]
    }
    setSelectedOptions(newSelectedOptions)
    // Call the onChange function with the selected options' values
    if (onChange) {
      onChange(newSelectedOptions?.map((option) => option.value))
    }
  }

  return (
    <div
      ref={wrapperRef}
      className={`relative w-[100%] ${disabled ? 'pointer-events-none' : ''}`}
    >
      <label className="text-xs font-medium text-text1">{title}</label>
      <div
        className={`w-[100%] mt-[10px] h-auto p-3 border rounded-xl flex gap-2 items-center cursor-pointer ${disabled ? 'bg-gray2' : ''} ${
          error ? 'border-textError' : 'border-gray3'
        }`}
        onClick={toggleSelect}
      >
        {iconName && (
          <Icon
            name={iconName}
            width={16}
            height={16}
            strokeWidth={2.67}
            stroke={error ? '#FF6666' : '#02698C'}
          />
        )}
        <div className="flex-1">
          {selectedOptions.length === 0 ? (
            <span className="text-[14px] text-text3">
              {placeholder ? placeholder : 'Select option(s)'}
            </span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {selectedOptions.map((option, index) => (
                <>
                  <span
                    key={index}
                    className={`flex gap-2 items-center rounded-lg px-3 py-1 bg-gray2 ${selectedOptionClass}`}
                  >
                    <p className="text-sm font-medium text-bluishGreen">
                      {option.label}
                    </p>

                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOptionClick(option)
                      }}
                    >
                      <Icon
                        name={'IconCross'}
                        width={8}
                        height={8}
                        strokeWidth={2}
                        stroke="#02698C"
                      />
                    </div>
                  </span>
                </>
              ))}
            </div>
          )}
        </div>
        <Icon
          name={'IconArrowDown'}
          width={16}
          height={16}
          strokeWidth={1}
          stroke="#02698C"
        />
      </div>
      {isOpen && (
        <div className="absolute mt-1 w-full bg-white border max-h-[300px] overflow-hidden overflow-y-scroll overflow-x-hidden border-gray3 rounded-md shadow-lg z-10">
          {options.map((option, index) => (
            <div
              key={index}
              className={`p-2 flex items-center gap-2 cursor-pointer hover:bg-gray1 m-1 hover:rounded-sm group`}
              onClick={() => handleOptionClick(option)}
            >
              {option.iconName && (
                <Icon
                  name={option.iconName}
                  width={16}
                  height={16}
                  strokeWidth={1}
                  stroke={'#02698C'}
                />
              )}
              <p
                className={`font-semibold text-[14px] text-text3 group-hover:text-text2 flex-1 ${labelClass}`}
              >
                {option.label}
              </p>
              {selectedOptions.some(
                (selectedOption) => selectedOption.value === option.value
              ) && (
                <Icon
                  name={'IconCheck'}
                  width={16}
                  height={16}
                  strokeWidth={1}
                  stroke={'#02698C'}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default UIMultiSelect
